'use client'
import { type FC, useMemo } from 'react'
import { Box, VStack, Wrap, WrapItem } from '@chakra-ui/react'
import { usePathname } from 'next/navigation'
import {
  ContentContainer,
  GrandAccordion,
  GrandHeading,
  GrandNextLink,
  type IAccordionItem
} from 'ui/common'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { useUIGlobalsContext } from 'ui/lib/ui-globals/hooks'
import { type UITopNavMenuItem } from '../types'
import { type SeeAllOverrides } from '../../../types'
import CategoryWithSubCategories from './CategoryWithSubCategories'

const currentStyles = {
  color: 'primary',
  fontWeight: 'bold'
}

export interface ISlideOutMobileMenuProps {
  menuItems: UITopNavMenuItem[]
  paths: {
    categories: string
  }
}

export const MenuStandardSlideOutMobileMenu: FC<ISlideOutMobileMenuProps> = ({
  menuItems,
  paths
}) => {
  const overrides = useOverridesContext<keyof SeeAllOverrides>()
  const pathname = usePathname()
  const { menuMaxContentWidth } = useUIGlobalsContext()

  const { itemsWithSubcategories, otherItems } = useMemo(
    () =>
      menuItems.reduce<{
        itemsWithSubcategories: UITopNavMenuItem[]
        otherItems: UITopNavMenuItem[]
      }>(
        (acc, item) => {
          if (item.type === 'store-front-category' && !!item.originalData?.subCategories?.length) {
            acc.itemsWithSubcategories.push(item)
          } else {
            acc.otherItems.push(item)
          }

          return acc
        },
        { itemsWithSubcategories: [], otherItems: [] }
      ),
    [menuItems]
  )

  const accordionItems: IAccordionItem[] = useMemo(() => {
    return itemsWithSubcategories.map((item) => ({
      id: item.id,
      title: item.text.toUpperCase(),
      buttonProps: pathname?.includes(item?.path) ? currentStyles : {},
      component: (
        <>
          <Box pb={2}>
            <GrandNextLink aria-label={`${item.text}-category`} href={item.path}>
              {overrideText('Se alla', overrides?.seeAll)}
            </GrandNextLink>
          </Box>
          <Wrap flex={1} spacing={4}>
            {item.originalData?.subCategories?.map((category, index) => (
              <WrapItem key={`${index}-navCategoryGroup1-${category.id}`}>
                <CategoryWithSubCategories {...{ category, paths }} />
              </WrapItem>
            ))}
          </Wrap>
        </>
      )
    }))
  }, [itemsWithSubcategories, overrides?.seeAll, pathname, paths])

  return (
    <ContentContainer direction="column" p={0} maxW={menuMaxContentWidth}>
      <VStack align="flex-start" pl="12px">
        <GrandAccordion allowToggle items={accordionItems} w="full" />

        {otherItems.map((item) => (
          <GrandNextLink aria-label={item.text} href={item.path} key={item.id} px={4} w="full">
            <GrandHeading
              cursor="pointer"
              fontSize={{
                base: 'sm',
                md: 'lg'
              }}
              headingTag="h4"
              textTransform="uppercase"
              title={item.text}
              {...(pathname === item?.path && currentStyles)}
            />
          </GrandNextLink>
        ))}
      </VStack>
    </ContentContainer>
  )
}
